import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import * as S from './styles';

import {
  PDFViewerProps,
  PDFViewerRef,
  ViewerStateProps,
} from 'components/ListDocumentsViewer/utils/interfaces';
import useSnackBar from 'hooks/useSnackBar';
import { usePDFRenderer } from 'components/ListDocumentsViewer/hooks/usePDFRenderer';
import { PageRenderer } from '../PageRenderer';
import { CircularProgress } from '@material-ui/core';

export const DocumentView = forwardRef<PDFViewerRef, PDFViewerProps>(
  (
    {
      fileUrl,
      showSinglePage = false,
      setListDocumentsViewerState,
      isLoading = true,
      listDocumentsViewerState,
    },
    ref
  ) => {
    const viewerRef = useRef<HTMLDivElement>(null);

    const { handleSnackBarError } = useSnackBar();

    const isZooming = useRef(false);

    const [viewerState, setViewerState] = useState<ViewerStateProps>({
      scale: listDocumentsViewerState?.scale,
      rotation: 0,
    });

    const { pdfDoc, renderPageCanvas } = usePDFRenderer({
      fileUrl,
      scale: viewerState.scale,
      rotation: viewerState.rotation,
      setListDocumentsViewerState,
      showSnackbar: handleSnackBarError,
    });

    useEffect(() => {
      setListDocumentsViewerState(prev => ({
        ...prev,
        scale: viewerState.scale,
      }));
    }, [viewerState.scale, setListDocumentsViewerState]);

    useEffect(() => {
      setViewerState({ scale: 1.5, rotation: 0 });
    }, [fileUrl]);

    const lockZoom = (callback: () => void) => {
      if (isZooming.current) return;
      isZooming.current = true;
      callback();
      setTimeout(() => {
        isZooming.current = false;
      }, 300);
    };

    useImperativeHandle(ref, () => ({
      zoomIn: () =>
        lockZoom(() =>
          setViewerState(prev => ({ ...prev, scale: prev.scale + 0.25 }))
        ),
      zoomOut: () =>
        lockZoom(() =>
          setViewerState(prev => ({
            ...prev,
            scale: Math.max(prev.scale - 0.25, 0.25),
          }))
        ),
      rotateClockwise: () =>
        setViewerState(prev => ({
          ...prev,
          rotation: (prev.rotation + 90) % 360,
        })),
      rotateCounterClockwise: () =>
        setViewerState(prev => ({
          ...prev,
          rotation: (prev.rotation - 90 + 360) % 360,
        })),
    }));

    const pages = useMemo(
      () =>
        Array.from(
          { length: showSinglePage ? 1 : pdfDoc?.numPages || 0 },
          (_, index) => (
            <PageRenderer
              key={index}
              index={index}
              renderPageCanvas={renderPageCanvas}
              showSinglePage={showSinglePage}
            />
          )
        ),
      [pdfDoc, renderPageCanvas, showSinglePage]
    );

    return (
      <S.ViewerContainer>
        {(!pdfDoc || isLoading) && (
          <S.LoadingOverlay>
            <CircularProgress color="primary" />
          </S.LoadingOverlay>
        )}
        {!isLoading && pdfDoc && (
          <S.ContainerPagesDocument
            ref={viewerRef}
            data-show-single-page={showSinglePage}
          >
            {pages}
          </S.ContainerPagesDocument>
        )}
      </S.ViewerContainer>
    );
  }
);
